import { isWorkspaceMembershipOffline } from "@/utils/workspaceMembershipOffline";

type VolatileHeartbeatRow = {
  aliasMembershipId: string | null;
  mostRecentHandsFreeStatusUpdatedAt: string | Date | null;
};

/**
 * Membership ids from the unactivated set that are freshly heartbeating.
 *
 * A fresh heartbeat only arrives over an authenticated device session, so it is live proof the member has
 * logged in while the REST-fetched unactivated set still says otherwise — the signal DriverView and
 * `useDispatchRows` use to refetch that set immediately. Staleness matters: unactivated memberships can carry historical availability
 * rows (e.g. an account disconnected after logging in), and those must not signal. Uses the same staleness
 * rule (`isWorkspaceMembershipOffline`) the cell's formatter delegates to.
 */
export function membershipIdsWithFreshHeartbeat(
  volatileRows: readonly VolatileHeartbeatRow[],
  unactivatedMembershipIds: ReadonlySet<string>,
): Set<string> {
  const fresh = new Set<string>();
  if (unactivatedMembershipIds.size === 0) return fresh;
  for (const row of volatileRows) {
    if (row.aliasMembershipId == null || !unactivatedMembershipIds.has(row.aliasMembershipId)) continue;
    if (isWorkspaceMembershipOffline({ handsFreeUpdatedAt: row.mostRecentHandsFreeStatusUpdatedAt })) continue;
    fresh.add(row.aliasMembershipId);
  }
  return fresh;
}

/**
 * Ids in `fresh` that have not been signaled yet. The caller records what it signaled so each membership
 * triggers at most one refetch — if the server (pathologically) still reports the member unactivated, the
 * poll interval is the backstop, not a refetch loop.
 */
export function newlySignaledIds(fresh: ReadonlySet<string>, alreadySignaled: ReadonlySet<string>): string[] {
  return [...fresh].filter((id) => !alreadySignaled.has(id));
}
